import { useEffect, useMemo } from 'react';
import { Link, useLocation, useMatch, useNavigate } from 'react-router-dom';

import { currentTenantId } from '../api.js';
import { peekProjectContext, useProjectContext } from '../hooks/useProjectContext.js';
import { ProjectPicker, useTenantProjects } from './ProjectPicker.jsx';
import { TenantPicker, useMyTenants, HEADER_PICKER_CSS } from './TenantPicker.jsx';
import { UserMenu } from './UserMenu.jsx';

// Card 070 — only auto-redirect to the cached project from these
// "you just landed" surfaces.  Everywhere else (Profile, Audit,
// Members, /keys, /my-keys, /settings, /integrations, /billing,
// /email-domains, /webhooks, /jwt-decoder, /sdk-version) the user
// navigated deliberately and we leave them alone.
const TENANT_HOME_PATHS = new Set(['/', '/inbox', '/help']);

/**
 * Best-effort name for the current page (card spotlight-062 — the
 * user reported "I don't know what page I'm on any more").  Keyed
 * by the leading path segment so we don't have to thread page
 * metadata through every route component.  Project-scoped sub-pages
 * resolve to the sub-section because the project name is already in
 * the breadcrumb to the left.
 */
// Card 096 — every entry must match the corresponding sidebar
// item's label verbatim.  The audit script in scripts/check-nav.sh
// fails CI when sidebar / PAGE_LABELS / PROJECT_SUB_LABELS drift,
// so any future relabel ripples consistently across the chrome.
const PAGE_LABELS = [
  ['oauth/authorize', 'Authorize app'],
  ['projects', 'Projects'],
  ['members', 'Members'],
  ['knowledge-base', 'Knowledge base'],
  ['help/articles', 'Knowledge base'],
  ['help/feedback', 'Help feedback'],
  ['inbox', 'Inbox'],
  ['help', 'Inbox'],
  ['audit', 'Audit log'],
  ['settings', 'Tenant settings'],
  ['email-domains', 'Email domains'],
  ['users', 'User progress'],
  ['webhooks', 'Tenant webhooks'],
  ['keys', 'API keys'],
  ['my-keys', 'My API keys'],
  ['integrations', 'Integrations'],
  ['profile', 'Your profile'],
  ['billing', 'Billing'],
  ['install', 'Install'],
  ['sdk-version', 'SDK version'],
];

const PROJECT_SUB_LABELS = {
  tours: 'Tours',
  content: 'Content',
  themes: 'Themes',
  checklists: 'Checklists',
  'api-keys': 'API keys',
  sites: 'Sites',
  auth: 'Authentication',
  authentication: 'Authentication',
  help: 'Help widget',
  'help-widget': 'Help widget',
  webhooks: 'Webhooks',
  analytics: 'Tour analytics',
  install: 'Install',
  bookmarklet: 'Bookmarklet',
};

// Paths the "Back to <project>" link should NOT appear on.  These
// are personal/tenant pages the user might be navigating to FROM a
// project, but Profile + a project-scoped Help screen are exits the
// user typed deliberately — we don't want to nudge them right back.
// (Profile especially: the only thing that uniquely belongs there is
// personal account settings, which have nothing to do with a project.)
const PROJECT_AWARE_BACK_LINK_BLOCKLIST = new Set([
  '/',
  '/profile',
]);

function pageLabelFor(pathname) {
  const trimmed = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  if (trimmed === '') return 'Home';
  const segs = trimmed.split('/');
  if (segs[0] === 'projects' && segs.length >= 2) {
    if (segs.length === 2) return 'Project overview';
    const sub = segs[2];
    return PROJECT_SUB_LABELS[sub] || 'Project';
  }
  const sorted = [...PAGE_LABELS].sort((a, b) => b[0].length - a[0].length);
  for (const [prefix, label] of sorted) {
    if (trimmed === prefix || trimmed.startsWith(`${prefix}/`)) {
      return label;
    }
  }
  return null;
}

/**
 * Top header bar for every authenticated page (card 050).
 *
 *   ┌─────────────────────────────────────────────────────────┐
 *   │  Spotlight │ Tenant ▾  /  Project ▾    [ user menu ▾ ]  │
 *   └─────────────────────────────────────────────────────────┘
 *
 * Borrows the breadcrumb-style picker layout from Clerk's dashboard.
 * Replaces both the sidebar-mounted tenant switcher AND the sidebar
 * footer's UserButton + display-name strip from RFC-038.
 *
 * Three sections:
 *
 *   - LEFT  — brand mark + tenant + project breadcrumbs.
 *   - RIGHT — user popover (avatar + display name).
 *
 * The project breadcrumb only renders once a tenant is in scope —
 * the project list is tenant-scoped so showing a project picker
 * without a tenant chosen would be confusing.  Same rule the v1
 * sidebar enforced.
 */
export function HeaderBar() {
  const storedTenantId = currentTenantId();
  const projectMatch = useMatch('/projects/:slug/*');
  const activeProjectSlug = projectMatch?.params?.slug || null;
  const location = useLocation();
  const pageLabel = pageLabelFor(location.pathname);
  const navigate = useNavigate();

  const { tenants, isSuperAdmin, loading, reload } = useMyTenants();
  const { projects: tenantProjects } = useTenantProjects();

  // Card 068 — the breadcrumb only renders the project chip when we
  // can actually name the active tenant.  `currentTenantId()` returns
  // whatever's in localStorage / env default, which can be stale
  // (revoked membership, env switch, fresh session); resolving it
  // against the user's memberships is the truth we can trust.  While
  // tenants are still loading we treat tenant as unresolved so we
  // don't flash a chip from sessionStorage during boot.
  const activeTenant = useMemo(
    () => tenants.find((t) => t.tenant_id === storedTenantId) || null,
    [tenants, storedTenantId],
  );
  const resolvedTenantId = activeTenant?.tenant_id || null;

  // Card 064 — sticky project context.  When the user is currently
  // off a project route but recently visited one (e.g. they clicked
  // "My API keys" from a project page), we want to surface a one-
  // click "Back to <project>" link rather than make them re-pick.
  // Card 068 — the cache is now keyed by the resolved tenant; when
  // there's no tenant the hook returns null and the chip / pill stay
  // hidden regardless of what's in sessionStorage.
  const projectCtx = useProjectContext(resolvedTenantId);
  const showBackToProject = (
    resolvedTenantId
    && projectCtx
    && !projectCtx.isUrlActive
    && !PROJECT_AWARE_BACK_LINK_BLOCKLIST.has(location.pathname)
    // Don't suggest going back into a project route — useMatch
    // already covers the URL-active case, but defensive belt + braces.
    && !location.pathname.startsWith(`/projects/${projectCtx.slug}`)
  );

  // Card 070 — when a tenant resolves and the user is sitting on a
  // tenant-home surface (Inbox / Home), auto-restore the last project
  // they were in for that tenant.  No project for that tenant in the
  // sticky cache, or the cached slug isn't in the tenant's project
  // list anymore (deleted)?  Stay put.  We never bounce them off
  // pages they navigated to deliberately.
  useEffect(() => {
    if (!resolvedTenantId) return;
    if (activeProjectSlug) return;
    if (!TENANT_HOME_PATHS.has(location.pathname)) return;
    const cached = peekProjectContext(resolvedTenantId);
    if (!cached?.slug) return;
    if (!tenantProjects?.some((p) => p.slug === cached.slug)) return;
    navigate(`/projects/${cached.slug}`, { replace: true });
  }, [resolvedTenantId, activeProjectSlug, location.pathname, tenantProjects, navigate]);

  // Keep the browser tab title in sync with the page label.  The
  // tab strip is the "where am I" signal users glance at when they
  // alt-tab between windows; without this, every Spotlight tab
  // reads "Spotlight" and they pick the wrong one.
  useEffect(() => {
    const base = 'Spotlight';
    document.title = pageLabel ? `${pageLabel} · ${base}` : base;
  }, [pageLabel]);

  function toggleMobileNav() {
    if (typeof window === 'undefined') return;
    window.dispatchEvent(new CustomEvent('spotlight:mobile-nav', { detail: { state: 'toggle' } }));
  }

  return (
    <header className="hb">
      <div className="hb__left">
        <button
          type="button"
          className="hb__nav-toggle"
          aria-label="Open navigation menu"
          onClick={toggleMobileNav}
        >
          <HamburgerIcon />
        </button>
        <BrandMark />
        <TenantPicker
          tenants={tenants}
          isSuperAdmin={isSuperAdmin}
          loading={loading}
          activeTenantId={storedTenantId}
          onReload={reload}
        />
        {resolvedTenantId && (
          <ProjectPicker activeSlug={activeProjectSlug} />
        )}
        {pageLabel && (
          <>
            <span className="hb-picker__sep" aria-hidden="true">/</span>
            <span
              className="hb__page-label"
              aria-label={`Current page: ${pageLabel}`}
            >
              {pageLabel}
            </span>
          </>
        )}
      </div>

      <div className="hb__right">
        {showBackToProject && (
          <Link
            to={`/projects/${projectCtx.slug}`}
            className="hb__back-link"
            title={`Back to ${projectCtx.name}`}
          >
            <BackArrowIcon />
            <span>
              Back to <strong>{projectCtx.name}</strong>
            </span>
          </Link>
        )}
        <UserMenu />
      </div>

      <style>{HEADER_PICKER_CSS}</style>
      <style>{HEADER_BAR_CSS}</style>
    </header>
  );
}

function BackArrowIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  );
}

function BrandMark() {
  return (
    <a className="hb__brand" href="/" aria-label="Spotlight dashboard home">
      <span className="hb__brand-dot" aria-hidden="true" />
      <span className="hb__brand-name">Spotlight</span>
    </a>
  );
}

const HEADER_BAR_CSS = `
  .hb {
    grid-area: header;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    height: 56px;
    padding: 0 16px;
    background: var(--surface-0, #ffffff);
    border-bottom: 1px solid var(--border-1, #e2e8f0);
    position: sticky;
    top: 0;
    z-index: 50;
  }

  .hb__left {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    flex: 1 1 auto;
  }

  .hb__right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
  }

  .hb__brand {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 4px 10px 4px 6px;
    border-radius: var(--r-md, 10px);
    color: var(--text-1, #0f172a);
    text-decoration: none;
    font-weight: 700;
    font-size: 14.5px;
    letter-spacing: -0.01em;
    margin-right: 4px;
  }

  .hb__brand:hover {
    background: var(--surface-1, #f8fafc);
  }

  .hb__brand-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: var(--brand-600, #4f46e5);
    box-shadow: 0 0 0 4px var(--brand-100, #e0e7ff);
  }

  .hb__brand-name {
    display: inline-block;
  }

  /* Breadcrumb separator slash between tenant + project pickers. */
  .hb-picker__sep {
    color: var(--text-3, #64748b);
    font-size: 14px;
    padding: 0 2px;
    user-select: none;
  }

  .hb-picker--with-sep {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }

  /* Card 064 — "Back to <project>" link.  Renders next to the */
  /* UserMenu when the user wandered out of a project into a */
  /* personal page that has no project equivalent. */
  .hb__back-link {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px;
    border-radius: var(--r-pill, 999px);
    background: var(--surface-1, #f8fafc);
    color: var(--text-1, #0f172a);
    text-decoration: none;
    font-size: 12.5px;
    border: 1px solid var(--border-1, #e2e8f0);
    transition: background 120ms ease, border-color 120ms ease;
    max-width: 220px;
  }
  .hb__back-link strong {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 120px;
  }
  .hb__back-link:hover {
    background: var(--brand-50, #eef2ff);
    border-color: var(--brand-200, #c7d2fe);
    color: var(--brand-700, #4338ca);
  }
  @media (max-width: 720px) {
    .hb__back-link strong { max-width: 60px; }
  }

  /* Page-label pill — the "you are here" signal alongside the */
  /* tenant + project breadcrumbs. */
  .hb__page-label {
    font-size: 13.5px;
    font-weight: 600;
    color: var(--text-1, #0f172a);
    padding: 4px 10px;
    border-radius: var(--r-md, 8px);
    background: var(--surface-1, #f8fafc);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 280px;
  }

  /* Hamburger nav-toggle — hidden on desktop, shown on narrow */
  /* viewports as the entry point to the slide-over sidebar. */
  .hb__nav-toggle {
    display: none;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    margin-right: 2px;
    background: transparent;
    border: 0;
    border-radius: var(--r-md, 10px);
    color: var(--text-1, #0f172a);
    cursor: pointer;
  }
  .hb__nav-toggle:hover {
    background: var(--surface-1, #f8fafc);
  }
  .hb__nav-toggle:focus-visible {
    outline: 2px solid var(--brand-500, #6366f1);
    outline-offset: -2px;
  }

  @media (max-width: 720px) {
    .hb {
      padding: 0 8px max(0px, env(safe-area-inset-bottom)) 8px;
      gap: 6px;
    }
    .hb__nav-toggle { display: inline-flex; }
    .hb__brand-name { display: none; }
    .hb__brand { padding: 4px 6px; margin-right: 0; }
    .hb__page-label {
      max-width: 130px;
      font-size: 12.5px;
      padding: 3px 8px;
    }
    .hb-picker__sep { display: none; }
    .hb__back-link {
      padding: 4px 8px;
      font-size: 11.5px;
    }
  }
`;
